import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Typography,
  DatePicker,
  Button,
  Space,
  Table,
  Input,
  Tag,
  Tooltip,
  Row,
  Col,
  message,
  Switch,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function LaporanPertanggungjawabanStok() {
  const [range, setRange] = useState([]);
  const [nopen, setNopen] = useState("");
  const [kodeBarang, setKodeBarang] = useState("");
  const [namaBarang, setNamaBarang] = useState("");
  const [showZero, setShowZero] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  const fetchData = useCallback(
    async (
      page = 1,
      pageSize = 50,
      dateRange = range,
      nopenVal = nopen,
      kodeVal = kodeBarang,
      namaVal = namaBarang,
      zeroVal = showZero,
    ) => {
      setLoading(true);
      try {
        const user = getUser();
        const userLevel = String(user?.id_user_level || user?.id_level || "");

        const params = {
          page,
          limit: pageSize,
          show_zero: zeroVal ? 1 : 0,
        };

        if (
          dateRange &&
          dateRange.length === 2 &&
          dateRange[0] &&
          dateRange[1]
        ) {
          params.start_date = dateRange[0].format("YYYY-MM-DD");
          params.end_date = dateRange[1].format("YYYY-MM-DD");
        }
        if (nopenVal && nopenVal.trim()) {
          params.nopen = nopenVal.trim();
        }
        if (kodeVal && kodeVal.trim()) {
          params.kode_barang = kodeVal.trim();
        }
        if (namaVal && namaVal.trim()) {
          params.nama_barang = namaVal.trim();
        }
        if (userLevel === "7" && user.username) {
          params.id_customer = user.username;
        }

        const res = await axiosInstance.get("/report/stok-berjalan", {
          params,
        });
        let fetchedData = res.data.data || [];

        if (userLevel === "7") {
          const userNpwp = user.username;
          fetchedData = fetchedData.filter(
            (item) => item.npwp_importir === userNpwp,
          );
        }

        setData(fetchedData);
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.limit || pageSize,
          total: res.data.total || 0,
        });
      } catch (e) {
        console.error("fetchData stok error:", e);
        message.error("Gagal memuat laporan stok barang");
      } finally {
        setLoading(false);
      }
    },
    [range, nopen, kodeBarang, namaBarang, showZero],
  );

  useEffect(() => {
    fetchData(1, 50, range, nopen, kodeBarang, namaBarang, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchData(
      1,
      pagination.pageSize,
      range,
      nopen,
      kodeBarang,
      namaBarang,
      showZero,
    );
  };

  const handleReset = () => {
    setRange([]);
    setNopen("");
    setKodeBarang("");
    setNamaBarang("");
    setShowZero(false);
    fetchData(1, pagination.pageSize, [], "", "", "", false);
  };

  const handleTableChange = (newPagination) => {
    const page =
      newPagination.pageSize !== pagination.pageSize
        ? 1
        : newPagination.current;
    fetchData(
      page,
      newPagination.pageSize,
      range,
      nopen,
      kodeBarang,
      namaBarang,
      showZero,
    );
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const user = getUser();
      const userLevel = String(user?.id_user_level || user?.id_level || "");

      const params = {
        show_zero: showZero ? 1 : 0,
      };
      if (range && range.length === 2 && range[0] && range[1]) {
        params.start_date = range[0].format("YYYY-MM-DD");
        params.end_date = range[1].format("YYYY-MM-DD");
      }
      if (nopen && nopen.trim()) params.nopen = nopen.trim();
      if (kodeBarang && kodeBarang.trim())
        params.kode_barang = kodeBarang.trim();
      if (namaBarang && namaBarang.trim())
        params.nama_barang = namaBarang.trim();
      if (userLevel === "7" && user.username) {
        params.id_customer = user.username;
      }

      const response = await axiosInstance.get("/report/stok-berjalan/export", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      const startStr = params.start_date || "all";
      const endStr = params.end_date || "all";
      link.download = `laporan_stok_${startStr}_${endStr}.xlsx`;
      link.click();
      message.success("Export Excel berhasil diunduh");
    } catch (err) {
      console.error("Export error:", err);
      message.error("Gagal mengunduh file excel");
    } finally {
      setExportLoading(false);
    }
  };

  const formatNumber = (num, decimals = 0) => {
    const n = Number(num || 0);
    return n.toLocaleString("id-ID", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const columns = [
    {
      title: "No",
      key: "no",
      width: 55,
      align: "center",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "DOKUMEN PEMASUKAN",
      children: [
        {
          title: "Jenis",
          dataIndex: "jenis_dok_masuk",
          key: "jenis_dok_masuk",
          width: 80,
          align: "center",
          render: (val) => <Tag color="blue">{val || "BC 1.6"}</Tag>,
        },
        {
          title: "NOPEN",
          dataIndex: "dok_pabean_masuk",
          key: "dok_pabean_masuk",
          width: 100,
          align: "center",
          render: (val) => <Text strong>{val || "-"}</Text>,
        },
        {
          title: "TGL NOPEN",
          dataIndex: "tgl_dok_pabean_masuk",
          key: "tgl_dok_pabean_masuk",
          width: 100,
          align: "center",
          render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
          title: "TGL BPB",
          dataIndex: "tgl_bpb",
          key: "tgl_bpb",
          width: 100,
          align: "center",
          render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
          title: "Kode Barang",
          dataIndex: "item_code",
          key: "item_code",
          width: 120,
          align: "center",
          render: (val) => <Tag color="geekblue">{val}</Tag>,
        },
        {
          title: "Nama Barang",
          dataIndex: "item_name",
          key: "item_name",
          width: 240,
          ellipsis: { showTitle: false },
          render: (val) => (
            <Tooltip placement="topLeft" title={val}>
              <Text strong>{val || "-"}</Text>
            </Tooltip>
          ),
        },
        {
          title: "Seri",
          dataIndex: "seri",
          key: "seri",
          width: 80,
          align: "center",
          render: (val) => <Text strong>{val || "-"}</Text>,
        },
        {
          title: "Kemasan (Karton)",
          key: "kemasan_masuk",
          width: 125,
          align: "right",
          render: (_, r) => (
            <Space orientation="horizontal" size={4}>
              <Text strong>{formatNumber(r.nilai_kemasan_masuk)}</Text>
              <Text orientation="secondary" style={{ fontSize: 11 }}>
                {r.nama_kemasan || "CT"}
              </Text>
            </Space>
          ),
        },
        {
          title: "Subsatuan (Botol/Can)",
          key: "subsatuan_masuk",
          width: 140,
          align: "right",
          render: (_, r) => (
            <Space orientation="horizontal" size={4}>
              <Text>{formatNumber(r.nilai_subsatuan_masuk)}</Text>
              <Text orientation="secondary" style={{ fontSize: 11 }}>
                {r.nama_subsatuan || "BO"}
              </Text>
            </Space>
          ),
        },
        {
          title: "Satuan (LTR)",
          key: "satuan_masuk",
          width: 130,
          align: "right",
          render: (_, r) => (
            <Space orientation="horizontal" size={4}>
              <Text style={{ color: "#1890ff" }}>
                {formatNumber(r.nilai_satuan_masuk, 2)}
              </Text>
              <Text orientation="secondary" style={{ fontSize: 11 }}>
                {r.nama_satuan || "LTR"}
              </Text>
            </Space>
          ),
        },
      ],
    },
    {
      title: "DOKUMEN PENGELUARAN",
      children: [
        {
          title: "Jenis",
          dataIndex: "jenis_dok_keluar",
          key: "jenis_dok_keluar",
          width: 90,
          align: "center",
          render: (val, r) => {
            const label =
              val || (Number(r.nilai_kemasan_keluar) > 0 ? "BC 2.8" : "-");
            return label !== "-" ? <Tag color="green">{label}</Tag> : "-";
          },
        },
        {
          title: "NOPEN KELUAR",
          dataIndex: "dok_pabean_keluar",
          key: "dok_pabean_keluar",
          width: 140,
          ellipsis: { showTitle: false },
          render: (val) => (
            <Tooltip placement="topLeft" title={val}>
              <span>{val || "-"}</span>
            </Tooltip>
          ),
        },
        {
          title: "TGL NOPEN",
          dataIndex: "tgl_dok_pabean_keluar",
          key: "tgl_dok_pabean_keluar",
          width: 125,
          align: "center",
          ellipsis: { showTitle: false },
          render: (val) => {
            if (!val) return "-";
            const text = String(val).includes("/")
              ? val
              : dayjs(val).isValid()
                ? dayjs(val).format("DD/MM/YYYY")
                : val;
            return (
              <Tooltip placement="topLeft" title={text}>
                <span>{text}</span>
              </Tooltip>
            );
          },
        },
        {
          title: "TGL SJ",
          dataIndex: "tgl_surat_jalan",
          key: "tgl_surat_jalan",
          width: 125,
          align: "center",
          ellipsis: { showTitle: false },
          render: (val) => {
            if (!val) return "-";
            const text = String(val).includes("/")
              ? val
              : dayjs(val).isValid()
                ? dayjs(val).format("DD/MM/YYYY")
                : val;
            return (
              <Tooltip placement="topLeft" title={text}>
                <span>{text}</span>
              </Tooltip>
            );
          },
        },
        {
          title: "Kemasan Keluar",
          key: "kemasan_keluar",
          width: 125,
          align: "right",
          render: (_, r) => {
            const n = Number(r.nilai_kemasan_keluar || 0);
            if (n <= 0) return "-";
            return (
              <Space orientation="horizontal" size={4}>
                <Text strong>{formatNumber(n)}</Text>
                <Text orientation="secondary" style={{ fontSize: 11 }}>
                  {r.nama_kemasan || "CT"}
                </Text>
              </Space>
            );
          },
        },
        {
          title: "Subsatuan Keluar",
          key: "subsatuan_keluar",
          width: 140,
          align: "right",
          render: (_, r) => {
            const n = Number(r.nilai_subsatuan_keluar || 0);
            if (n <= 0) return "-";
            return (
              <Space orientation="horizontal" size={4}>
                <Text>{formatNumber(n)}</Text>
                <Text orientation="secondary" style={{ fontSize: 11 }}>
                  {r.nama_subsatuan || "BO"}
                </Text>
              </Space>
            );
          },
        },
        {
          title: "Satuan (LTR) Keluar",
          key: "satuan_keluar",
          width: 130,
          align: "right",
          render: (_, r) => {
            const n = Number(r.nilai_satuan_keluar || 0);
            if (n <= 0) return "-";
            return (
              <Space orientation="horizontal" size={4}>
                <Text style={{ color: "#fa8c16" }}>{formatNumber(n, 2)}</Text>
                <Text orientation="secondary" style={{ fontSize: 11 }}>
                  {r.nama_satuan || "LTR"}
                </Text>
              </Space>
            );
          },
        },
      ],
    },
    {
      title: "SALDO BARANG (TERSEDIA)",
      children: [
        {
          title: "Saldo Kemasan",
          key: "saldo_kemasan",
          width: 125,
          align: "right",
          render: (_, r) => {
            const val = Number(r.saldo_kemasan || 0);
            return (
              <Space orientation="horizontal" size={4}>
                <Text strong style={{ color: val > 0 ? "#52c41a" : "#8c8c8c" }}>
                  {formatNumber(val)}
                </Text>
                <Text orientation="secondary" style={{ fontSize: 11 }}>
                  {r.nama_kemasan || "CT"}
                </Text>
              </Space>
            );
          },
        },
        {
          title: "Saldo Subsatuan",
          key: "saldo_subsatuan",
          width: 140,
          align: "right",
          render: (_, r) => {
            const val = Number(r.saldo_subsatuan || 0);
            return (
              <Space orientation="horizontal" size={4}>
                <Text strong style={{ color: val > 0 ? "#52c41a" : "#8c8c8c" }}>
                  {formatNumber(val)}
                </Text>
                <Text orientation="secondary" style={{ fontSize: 11 }}>
                  {r.nama_subsatuan || "BO"}
                </Text>
              </Space>
            );
          },
        },
        {
          title: "Saldo Satuan (LTR)",
          key: "saldo_satuan",
          width: 130,
          align: "right",
          render: (_, r) => {
            const val = Number(r.saldo_satuan || 0);
            return (
              <Space orientation="horizontal" size={4}>
                <Text strong style={{ color: val > 0 ? "#1890ff" : "#8c8c8c" }}>
                  {formatNumber(val, 2)}
                </Text>
                <Text orientation="secondary" style={{ fontSize: 11 }}>
                  {r.nama_satuan || "LTR"}
                </Text>
              </Space>
            );
          },
        },
        {
          title: "LOT / RAK",
          dataIndex: "lokasi_lot",
          key: "lokasi_lot",
          width: 110,
          align: "center",
          render: (val) => (val ? <Tag color="purple">{val}</Tag> : "-"),
        },
      ],
    },
  ];

  return (
    <Card
      style={{ borderRadius: 10, marginBottom: 40 }}
      bodyStyle={{ padding: "20px" }}
    >
      {/* Header: Title, Description & Export Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div style={{ maxWidth: 850 }}>
          <Title
            level={4}
            style={{ marginBottom: 4, fontWeight: 700, color: "#1f2937" }}
          >
            Laporan Pertanggungjawaban Stok Barang
          </Title>
          <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
            Posisi saldo stok berjalan per dokumen pabean pemasukan dan
            pengeluaran (Kemasan, Subsatuan, Satuan).
          </Text>
        </div>
        <Button
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={exportLoading}
          style={{
            borderColor: "#52c41a",
            color: "#52c41a",
            fontWeight: 600,
            height: 38,
            borderRadius: 6,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          Export Excel
        </Button>
      </div>

      {/* Filter Card */}
      <Card
        size="small"
        style={{
          background: "#fafafa",
          borderRadius: 8,
          marginBottom: 20,
          border: "1px solid #e8e8e8",
        }}
        bodyStyle={{ padding: "16px 18px" }}
      >
        {/* Baris 1: 4 Kolom Filter Input yang Luas & Nyaman */}
        <Row gutter={[16, 14]}>
          <Col xs={24} sm={12} md={6}>
            <Text
              strong
              style={{
                fontSize: 12,
                display: "block",
                marginBottom: 6,
                color: "#4b5563",
              }}
            >
              Periode Dokumen
            </Text>
            <RangePicker
              value={range}
              onChange={(val) => setRange(val || [])}
              format="DD-MM-YYYY"
              allowClear
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text
              strong
              style={{
                fontSize: 12,
                display: "block",
                marginBottom: 6,
                color: "#4b5563",
              }}
            >
              No. Dokumen / Nopen
            </Text>
            <Input
              placeholder="Cari no. nopen / dokumen..."
              value={nopen}
              onChange={(e) => setNopen(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<FileTextOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text
              strong
              style={{
                fontSize: 12,
                display: "block",
                marginBottom: 6,
                color: "#4b5563",
              }}
            >
              Kode Barang
            </Text>
            <Input
              placeholder="Cari kode barang..."
              value={kodeBarang}
              onChange={(e) => setKodeBarang(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<BarcodeOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text
              strong
              style={{
                fontSize: 12,
                display: "block",
                marginBottom: 6,
                color: "#4b5563",
              }}
            >
              Nama Barang
            </Text>
            <Input
              placeholder="Cari nama barang..."
              value={namaBarang}
              onChange={(e) => setNamaBarang(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<ShoppingOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>
        </Row>

        {/* Divider pemisah antara input dan baris kontrol */}
        <div style={{ margin: "14px 0", height: 1, background: "#f0f0f0" }} />

        {/* Baris 2: Switch Stok 0 di Kiri & Tombol Aksi di Kanan */}
        <Row gutter={[12, 12]} justify="space-between" align="middle">
          {/* Switch On/Off Tampilkan Stok 0 */}
          <Col xs={24} sm={14} md={12}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                padding: "6px 14px",
                borderRadius: 8,
                background: showZero ? "#fff7e6" : "#f6ffed",
                border: `1px solid ${showZero ? "#ffd591" : "#b7eb8f"}`,
              }}
            >
              <Switch
                checked={showZero}
                onChange={(checked) => {
                  setShowZero(checked);
                  fetchData(
                    1,
                    pagination.pageSize,
                    range,
                    nopen,
                    kodeBarang,
                    namaBarang,
                    checked,
                  );
                }}
                checkedChildren="Tampil"
                unCheckedChildren="Sembunyi"
                style={{ backgroundColor: showZero ? "#fa8c16" : "#52c41a" }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Text
                  strong
                  style={{
                    fontSize: 13,
                    color: showZero ? "#d48806" : "#2e7d32",
                  }}
                >
                  {showZero
                    ? "Semua Barang Ditampilkan"
                    : "Hanya Barang dengan Stok Aktif"}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {showZero
                    ? "Termasuk data barang yang saldonya sudah 0"
                    : "Barang yang saldonya 0 otomatis disembunyikan"}
                </Text>
              </div>
            </div>
          </Col>

          {/* Tombol Cari Data dan Reset */}
          <Col xs={24} sm={10} md={12} style={{ textAlign: "right" }}>
            <Space
              size={10}
              wrap
              style={{ justifyContent: "flex-end", width: "100%" }}
            >
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                style={{
                  minWidth: 110,
                  height: 38,
                  borderRadius: 6,
                  fontWeight: 600,
                }}
              >
                Cari Data
              </Button>

              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                style={{
                  minWidth: 90,
                  height: 38,
                  borderRadius: 6,
                }}
              >
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Text
        type="secondary"
        style={{ fontSize: 11, display: "block", marginBottom: 8 }}
      >
        💡 Geser tabel ke kanan untuk melihat rincian pengeluaran dan saldo
        secara lengkap.
      </Text>

      <Table
        size="small"
        bordered
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={(record, index) =>
          `${record.dok_pabean_masuk || "in"}-${record.item_code || "item"}-${index}`
        }
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          responsive: true,
          pageSizeOptions: ["20", "50", "100", "200"],
          showTotal: (total, r) =>
            `${r[0]}-${r[1]} dari ${total.toLocaleString("id-ID")} data`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 2300 }}
      />
    </Card>
  );
}
